"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Mail, Phone, Send, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const bulletPoints = [{
  title: "100% Direct Access",
  desc: "You work directly with me, Yassine Essaadi. No account managers or junior pass-offs."
}, {
  title: "Tailored SEO Strategy",
  desc: "Custom growth roadmaps built around your revenue goals and market opportunity."
}, {
  title: "Honest & Practical Advice",
  desc: "Realistic expectations, clear deliverables, and zero agency fluff or vanity metrics."
}];
const quickStats = [{
  label: "Response Time",
  value: "< 24 Hours"
}, {
  label: "Consultation Fee",
  value: "Free (£0)"
}, {
  label: "Direct Consultant",
  value: "Yassine Essaadi"
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    register
  } = useWvcForm();
  if (isSubmitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="py-12 text-center space-y-4">
        <div className="size-16 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto">
          <ShieldCheck className="size-8" />
        </div>
        <h4 className="text-2xl font-bold font-serif text-foreground">
          Consultation Requested!
        </h4>
        <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed">
          {successMessage || "Thank you for reaching out. I have received your website details and will review your current search positioning personally within 24 hours."}
        </p>
        <div className="pt-4">
          <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted">
            Submit Another Request
          </Button>
        </div>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="mt-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="consultation-name" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Your Name <span className="text-destructive">*</span>
          </label>
          <input id="consultation-name" type="text" required placeholder="Yassine Essaadi" {...register("name", {
          required: true
        })} className="w-full px-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="consultation-email" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Email Address <span className="text-destructive">*</span>
          </label>
          <input id="consultation-email" type="email" required placeholder="yassine@example.com" {...register("email", {
          required: true
        })} className="w-full px-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="consultation-website" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Website URL <span className="text-destructive">*</span>
          </label>
          <input id="consultation-website" type="text" required placeholder="https://yourcompany.com" {...register("website", {
          required: true
        })} className="w-full px-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="consultation-service" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Primary Service Focus
          </label>
          <select id="consultation-service" {...register("service")} className="w-full px-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow">
            <option value="National SEO">National SEO</option>
            <option value="International SEO">International SEO</option>
            <option value="Local SEO">Local SEO</option>
            <option value="B2B SEO & Lead Gen">B2B SEO &amp; Lead Generation</option>
            <option value="Ecommerce SEO">Ecommerce SEO</option>
            <option value="Technical SEO">Technical SEO</option>
            <option value="SEO Audits">SEO Audits</option>
            <option value="AI Search Optimisation">AI Search Optimisation</option>
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="consultation-message" className="text-xs font-semibold text-foreground uppercase tracking-wider">
          Current SEO Challenges or Goals
        </label>
        <textarea id="consultation-message" rows={3} placeholder="Tell me briefly about your business and what you hope to achieve with SEO..." {...register("message")} className="w-full px-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow resize-none" />
      </div>

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <div className="pt-2 space-y-3">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-accent text-accent-foreground hover:bg-accent/90 shadow-md transition-[background-color,transform,box-shadow] active:scale-[0.99] group">
          <span>Book a Free SEO Consultation</span>
          <Send className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
        </Button>

        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span className="flex items-center gap-1">
            <ShieldCheck className="size-3.5 text-primary" />
            100% Confidential
          </span>
          <span>Direct review by Yassine</span>
        </div>
      </div>
    </form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-t border-border">
      <div className="aria-hidden:true pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-accent/15 blur-3xl" />
      <div className="aria-hidden:true pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <div className="lg:col-span-6 space-y-8">
            <motion.div variants={itemVariants} className="space-y-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary/80 border border-border">
                <UserCheck className="size-3.5 mr-1.5 text-accent" />
                Work Directly With Yassine
              </Badge>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground leading-tight">
                Book a Free <span className="text-primary">SEO Consultation</span> &amp; Strategy Review
              </h2>

              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Ready to increase your search rankings and capture sustainable organic revenue? 
                When you reach out, you speak directly with me. I will manually inspect your site, 
                analyse your search visibility, and share actionable next steps—no high-pressure sales pitch.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              {bulletPoints.map((item, i) => <div data-index={i} key={item.title} className="flex items-start gap-3.5 p-3.5 rounded-xl bg-card border border-border/60 transition-[colors,box-shadow] hover:border-primary/30 hover:shadow-sm">
                  <div className="mt-0.5 shrink-0 rounded-full bg-primary/10 p-1.5 text-primary">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-snug">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </motion.div>

            <motion.div variants={itemVariants} className="p-6 rounded-2xl bg-muted/40 border border-border space-y-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Sparkles className="size-4 text-accent" />
                Prefer Direct Contact?
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border text-foreground hover:border-primary transition-colors text-xs sm:text-sm font-medium">
                  <Mail className="size-4 text-primary shrink-0" />
                  <span className="truncate">contact@ye-consultancy.com</span>
                </a>
                <a href="tel:+447490984430" className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border text-foreground hover:border-primary transition-colors text-xs sm:text-sm font-medium">
                  <Phone className="size-4 text-primary shrink-0" />
                  <span>+44 7490 984430</span>
                </a>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="grid grid-cols-3 gap-2 pt-2 text-center">
              {quickStats.map((stat, i) => <div data-index={i} key={stat.label} className="p-3 rounded-xl bg-card border border-border">
                  <div className="text-xs text-muted-foreground font-medium">
                    {stat.label}
                  </div>
                  <div className="text-sm sm:text-base font-bold text-foreground mt-0.5">
                    {stat.value}
                  </div>
                </div>)}
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="lg:col-span-6 bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-xl relative">
            <div className="flex items-center justify-between pb-6 border-b border-border">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
                  Request Your Free Audit
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Fill in your details for a tailored SEO performance review.
                </p>
              </div>
              <Badge variant="outline" className="hidden sm:inline-flex items-center gap-1.5 border-accent/40 bg-accent/10 text-accent-foreground text-xs px-2.5 py-1">
                <Clock className="size-3 text-accent" />
                No Obligation
              </Badge>
            </div>

            <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
            name: "",
            email: "",
            website: "",
            service: "National SEO",
            message: ""
          }} successMessage="Thank you for reaching out. I have received your website details and will review your current search positioning personally within 24 hours." errorMessage="Something went wrong. Please try again.">
              <ConsultationFormContent />
            </FormProvider>

            <Separator className="my-6" />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>Want to see verified results first?</span>
              <Link to="/CaseStudiesPage" className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                <span>Explore My Case Studies</span>
                <ArrowRight className="size-3" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}